/**
 * Mantine Theme Configuration
 * Extends Mantine's default theme with custom design tokens
 */

import type { MantineThemeOverride } from '@mantine/core';
import { tokens } from './tokens';

export const theme: MantineThemeOverride = {
  primaryColor: 'violet',
  
  // Spacing scale
  spacing: {
    xs: `${tokens.spacing.xs}px`,
    sm: `${tokens.spacing.sm}px`,
    md: `${tokens.spacing.md}px`,
    lg: `${tokens.spacing.lg}px`,
    xl: `${tokens.spacing.xl}px`,
  },
  
  // Font sizes
  fontSizes: {
    xs: `${tokens.fontSizes.xs}px`,
    sm: `${tokens.fontSizes.sm}px`,
    md: `${tokens.fontSizes.md}px`,
    lg: `${tokens.fontSizes.lg}px`,
    xl: `${tokens.fontSizes.xl}px`,
  },
  
  // Line heights
  lineHeights: {
    xs: tokens.lineHeights.xs,
    sm: tokens.lineHeights.sm,
    md: tokens.lineHeights.md,
    lg: tokens.lineHeights.lg,
    xl: tokens.lineHeights.xl,
  },
  
  // Font weights
  fontWeights: {
    normal: tokens.fontWeights.normal,
    medium: tokens.fontWeights.medium,
    semibold: tokens.fontWeights.semibold,
    bold: tokens.fontWeights.bold,
  },
  
  // Radius
  radius: {
    xs: `${tokens.radius.xs}px`,
    sm: `${tokens.radius.sm}px`,
    md: `${tokens.radius.md}px`,
    lg: `${tokens.radius.lg}px`,
    xl: `${tokens.radius.xl}px`,
  },
  
  // Shadows - theme-aware (will be overridden by colorScheme)
  shadows: {
    xs: tokens.shadows.xs,
    sm: tokens.shadows.sm,
    md: tokens.shadows.md,
    lg: tokens.shadows.lg,
    xl: tokens.shadows.xl,
  },
  
  // Other theme properties for dark mode customization
  other: {
    shadowsLight: tokens.shadowsLight,
    shadowsDark: tokens.shadowsDark,
    opacity: tokens.opacity,
    gradients: tokens.gradients,
  },
  
  // Colors - extend with semantic colors
  colors: {
    violet: [
      '#f3f0ff',
      '#e5dbff',
      '#d0bfff',
      '#b197fc',
      '#9775fa',
      '#705fba',
      '#5d4e96',
      '#4a3d78',
      '#382d5f',
      '#2b2347',
    ],
    // Semantic colors
    success: [
      '#ecfdf5',
      '#d1fae5',
      '#a7f3d0',
      '#6ee7b7',
      '#34d399',
      '#10b981',
      '#059669',
      '#047857',
      '#065f46',
      '#064e3b',
    ],
    error: [
      '#fef2f2',
      '#fee2e2',
      '#fecaca',
      '#fca5a5',
      '#f87171',
      '#ef4444',
      '#dc2626',
      '#b91c1c',
      '#991b1b',
      '#7f1d1d',
    ],
    warning: [
      '#fffbeb',
      '#fef3c7',
      '#fde68a',
      '#fcd34d',
      '#fbbf24',
      '#f59e0b',
      '#d97706',
      '#b45309',
      '#92400e',
      '#78350f',
    ],
    info: [
      '#eff6ff',
      '#dbeafe',
      '#bfdbfe',
      '#93c5fd',
      '#60a5fa',
      '#3b82f6',
      '#2563eb',
      '#1d4ed8',
      '#1e40af',
      '#1e3a8a',
    ],
  },
  
  // Default component styles
  defaultRadius: 'md',
  
  // Headings
  headings: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: `${tokens.fontSizes['5xl']}px`, lineHeight: tokens.lineHeights.md },
      h2: { fontSize: `${tokens.fontSizes['4xl']}px`, lineHeight: tokens.lineHeights.md },
      h3: { fontSize: `${tokens.fontSizes['3xl']}px`, lineHeight: tokens.lineHeights.md },
      h4: { fontSize: `${tokens.fontSizes['2xl']}px`, lineHeight: tokens.lineHeights.md },
      h5: { fontSize: `${tokens.fontSizes.xl}px`, lineHeight: tokens.lineHeights.md },
      h6: { fontSize: `${tokens.fontSizes.lg}px`, lineHeight: tokens.lineHeights.md },
    },
  },
  
  // Component defaults
  components: {
    Button: {
      defaultProps: {
        radius: 'md',
      },
      styles: (theme) => ({
        root: {
          fontWeight: 500,
          transition: `all ${tokens.transitions.normal}ms ${tokens.easings.easeInOut}`,
        },
      }),
    },
    Paper: {
      defaultProps: {
        radius: 'md',
        shadow: 'sm',
        withBorder: true,
      },
    },
    Card: {
      defaultProps: {
        radius: 'md',
        shadow: 'sm',
        withBorder: true,
        padding: 'md',
      },
    },
    Input: {
      defaultProps: {
        radius: 'md',
      },
    },
    Select: {
      defaultProps: {
        radius: 'md',
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'md',
      },
    },
    Textarea: {
      defaultProps: {
        radius: 'md',
      },
    },
    Badge: {
      defaultProps: {
        radius: 'md',
      },
    },
    Alert: {
      defaultProps: {
        radius: 'md',
      },
    },
    Modal: {
      defaultProps: {
        radius: 'md',
        overlayProps: {
          backgroundOpacity: 0.5,
          blur: 4,
        },
      },
    },
    Drawer: {
      defaultProps: {
        overlayProps: {
          backgroundOpacity: 0.5,
          blur: 4,
        },
      },
    },
    Tabs: {
      styles: (theme) => ({
        tab: {
          fontWeight: 500,
          transition: `all ${tokens.transitions.normal}ms ${tokens.easings.easeInOut}`,
        },
      }),
    },
    Table: {
      defaultProps: {
        highlightOnHover: true,
        striped: false,
      },
      styles: (theme) => ({
        thead: {
          backgroundColor: theme.colorScheme === 'dark' 
            ? theme.colors.dark[7] 
            : theme.colors.gray[0],
        },
        tbody: {
          'tr:hover': {
            backgroundColor: theme.colorScheme === 'dark'
              ? theme.colors.dark[6]
              : theme.colors.gray[0],
            transition: `background-color ${tokens.transitions.fast}ms ${tokens.easings.easeInOut}`,
          },
        },
      }),
    },
    Notification: {
      styles: (theme) => ({
        root: {
          boxShadow: theme.colorScheme === 'dark'
            ? tokens.shadowsDark.md
            : tokens.shadowsLight.md,
          transition: `all ${tokens.transitions.normal}ms ${tokens.easings.easeInOut}`,
        },
      }),
    },
    Paper: {
      styles: (theme) => ({
        root: {
          boxShadow: theme.colorScheme === 'dark'
            ? tokens.shadowsDark.sm
            : tokens.shadowsLight.sm,
          transition: `box-shadow ${tokens.transitions.normal}ms ${tokens.easings.easeInOut}, background-color ${tokens.transitions.normal}ms ${tokens.easings.easeInOut}`,
        },
      }),
    },
    Card: {
      styles: (theme) => ({
        root: {
          boxShadow: theme.colorScheme === 'dark'
            ? tokens.shadowsDark.sm
            : tokens.shadowsLight.sm,
          transition: `box-shadow ${tokens.transitions.normal}ms ${tokens.easings.easeInOut}, background-color ${tokens.transitions.normal}ms ${tokens.easings.easeInOut}`,
        },
      }),
    },
    Modal: {
      styles: (theme) => ({
        content: {
          boxShadow: theme.colorScheme === 'dark'
            ? tokens.shadowsDark.xl
            : tokens.shadowsLight.xl,
        },
        overlay: {
          backgroundColor: theme.colorScheme === 'dark'
            ? 'rgba(0, 0, 0, 0.7)'
            : 'rgba(0, 0, 0, 0.5)',
        },
      }),
    },
    Drawer: {
      styles: (theme) => ({
        content: {
          boxShadow: theme.colorScheme === 'dark'
            ? tokens.shadowsDark.lg
            : tokens.shadowsLight.lg,
        },
        overlay: {
          backgroundColor: theme.colorScheme === 'dark'
            ? 'rgba(0, 0, 0, 0.7)'
            : 'rgba(0, 0, 0, 0.5)',
        },
      }),
    },
  },
  
  // Other theme properties
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  
  // Respect user's color scheme preference
  respectReducedMotion: true,
  
  // Cursor
  cursorType: 'default',
  
  // Dark mode color overrides for better contrast
  black: '#1a1a1a',
  white: '#ffffff',
  
  // Default colors for better theme consistency
  defaultGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
};

